import { Contact } from "@/types/contacts";
import Papa from 'papaparse';
import { applyMapping, FieldMapping } from './columnMapper';

const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
};

const isHeaderLine = (line: string): boolean => {
  const normalized = normalizeText(line);
  const headerKeywords = ['empresa', 'company', 'nome', 'name', 'email', 'telefone', 'phone', 'posicao', 'position'];
  return headerKeywords.some(keyword => normalized.includes(keyword));
};

const isValidDataLine = (columns: string[], companyIdx: number): boolean => {
  if (columns.length < 2) return false;
  
  const company = columns[companyIdx];
  if (!company) return false;
  
  // Reject if company is a header keyword
  const normalized = normalizeText(company);
  if (normalized === 'empresa' || normalized === 'company') return false;
  
  // Reject if company is only numbers (likely bad data)
  if (/^\d+$/.test(company)) return false;
  
  return true;
};

export const parseContactsCSV = (
  csvText: string,
  entityType: 'restaurant' | 'operator' | 'consolidator'
): Contact[] => {
  const lines = csvText.split('\n').filter(line => line.trim());
  
  if (lines.length === 0) return [];

  // Get header line
  const headers = lines[0].split(',').map(h => h.trim());
  
  console.log('📋 Parsing CSV with headers:', headers);
  
  // Find column indices
  const getColumnIndex = (possibleNames: string[]): number => {
    return headers.findIndex(header => 
      possibleNames.some(name => normalizeText(header).includes(normalizeText(name)))
    );
  };

  const companyIdx = getColumnIndex(['EMPRESA', 'empresa', 'company']);
  const firstNameIdx = getColumnIndex(['Nome', 'nome', 'first name', 'firstname']);
  const lastNameIdx = getColumnIndex(['Sobrenome', 'sobrenome', 'last name', 'lastname']);
  const positionIdx = getColumnIndex(['Posição', 'posicao', 'position', 'cargo']);
  const cellPhoneIdx = getColumnIndex(['Telefono celular', 'telefone celular', 'celular', 'cell phone', 'mobile']);
  const phoneIdx = getColumnIndex(['Telefone', 'telefone', 'phone', 'fixo']);
  const emailIdx = getColumnIndex(['E-mail', 'email', 'e-mail']);
  const websiteIdx = getColumnIndex(['Site', 'site', 'website', 'url']);
  const cityIdx = getColumnIndex(['Cidade', 'cidade', 'city']);
  const stateIdx = getColumnIndex(['Estado', 'estado', 'state', 'uf']);

  const contacts: Contact[] = [];
  let currentRegion = '';
  let currentState = '';

  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    // Skip if this looks like a header line
    if (isHeaderLine(line)) {
      console.log('⏭️ Skipping header line:', line);
      continue;
    }

    // Check if this is a region header
    const regionMatch = line.match(/^(Norte|Nordeste|Centro-Oeste|Sudeste|Sul)/i);
    if (regionMatch) {
      currentRegion = regionMatch[1];
      console.log('📍 Region detected:', currentRegion);
      continue;
    }

    // Check if this is a state header
    const stateMatch = line.match(/^([A-Z]{2})\s*$/);
    if (stateMatch) {
      currentState = stateMatch[1];
      console.log('🏛️ State detected:', currentState);
      continue;
    }

    const columns = line.split(',').map(col => col.trim());

    // Skip if doesn't pass validation
    if (!isValidDataLine(columns, companyIdx)) {
      console.log('❌ Invalid data line skipped:', line.substring(0, 50));
      continue;
    }

    const contact: Contact = {
      id: 0, // Will be set by database
      company: columns[companyIdx] || '',
      firstName: firstNameIdx >= 0 ? columns[firstNameIdx] || '' : '',
      lastName: lastNameIdx >= 0 ? columns[lastNameIdx] || '' : '',
      position: positionIdx >= 0 ? columns[positionIdx] || '' : '',
      cellPhone: cellPhoneIdx >= 0 ? columns[cellPhoneIdx] || '' : '',
      phone: phoneIdx >= 0 ? columns[phoneIdx] || '' : '',
      email: emailIdx >= 0 ? columns[emailIdx] || '' : '',
      website: websiteIdx >= 0 ? columns[websiteIdx] || '' : '',
      city: cityIdx >= 0 ? columns[cityIdx] || '' : '',
      state: stateIdx >= 0 && columns[stateIdx] ? columns[stateIdx] : currentState,
      entityType,
      region: currentRegion,
    };

    // Only add if has at least company name
    if (contact.company) {
      contacts.push(contact);
      console.log('✅ Contact parsed:', contact.company);
    }
  }

  console.log(`📊 Total contacts parsed: ${contacts.length}`);
  return contacts;
};

export const exportContactsToCSV = (contacts: Contact[]): string => {
  const headers = [
    'EMPRESA',
    'Nome',
    'Sobrenome',
    'Posição',
    'Telefone celular',
    'Telefone',
    'E-mail',
    'Site',
    'Cidade',
    'Estado'
  ].join(',');

  const rows = contacts.map(contact => {
    return [
      contact.company,
      contact.firstName,
      contact.lastName,
      contact.position,
      contact.cellPhone,
      contact.phone,
      contact.email,
      contact.website,
      contact.city,
      contact.state
    ].map(field => `"${field || ''}"`).join(',');
  });

  return [headers, ...rows].join('\n');
};

// Nova função para parsing com mapeamento de colunas
export const parseContactsCSVWithMapping = (
  csvText: string,
  mapping: FieldMapping,
  entityType: 'restaurant' | 'operator' | 'consolidator'
): Contact[] => {
  console.log('📋 Parsing CSV with column mapping');
  
  // Parse usando PapaParse para lidar melhor com CSVs
  const result = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header: string) => header.trim()
  });

  if (result.errors.length > 0) {
    console.warn('⚠️ CSV parsing warnings:', result.errors);
  }

  const contacts: Contact[] = [];

  result.data.forEach((row: any, index: number) => {
    try {
      // Aplica o mapeamento
      const mappedData = applyMapping(row, mapping);
      
      // Valida se tem campos mínimos
      if (!mappedData.email && !mappedData.first_name) {
        console.warn(`Linha ${index + 2} ignorada: faltam campos obrigatórios`);
        return;
      }

      const contact: Contact = {
        id: 0,
        company: mappedData.company || '',
        firstName: mappedData.first_name || '',
        lastName: mappedData.last_name || '',
        position: mappedData.position || '',
        cellPhone: mappedData.cell_phone || '',
        phone: mappedData.phone || '',
        email: mappedData.email || '',
        website: mappedData.website || '',
        city: mappedData.city || '',
        state: mappedData.state || '',
        entityType,
        region: mappedData.region || '',
        // Novos campos
        postalCode: mappedData.postal_code || undefined,
        country: mappedData.country || undefined,
        addressLine1: mappedData.address_line_1 || undefined,
        addressLine2: mappedData.address_line_2 || undefined,
        externalId: mappedData.external_id || undefined,
        anonymousId: mappedData.anonymous_id || undefined,
      };

      contacts.push(contact);
      console.log(`✅ Contact parsed: ${contact.firstName} ${contact.lastName} (${contact.email})`);
    } catch (error) {
      console.error(`Erro ao processar linha ${index + 2}:`, error);
    }
  });

  console.log(`📊 Total de contatos parseados: ${contacts.length}`);
  return contacts;
};
